import type { EntityTag, EntityTagCreate, EntityTagUpdate } from "./entity-tag";
import { EventStatus } from "./enums/event";
import { PageParams } from "./pagination";
import { PipelineStageSequenceSave } from "./pipeline-sequence";
import { ProviderTiny } from "./provider";

// ============================================================================
// Pipeline & Stage Types (clinic-configurable, no hardcoded enums)
// ============================================================================

export interface PipelineStageSummary {
  id: string;
  name: string;
  stageNumber: number;
  color?: string | null;
  kind: StageKind;
  staleAfterDays?: number | null;
  isTerminalWon: boolean;
  isTerminalLost: boolean;
  autoConvert: boolean;
}

/** What a pipeline does when someone already on it comes in as a new lead. */
export enum ReturningLeadPolicy {
  KEEP_STAGE = "KEEP_STAGE",
  RESTART = "RESTART",
}

export interface PipelineSummary {
  locationId?: string | null;
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  returningLeadPolicy: ReturningLeadPolicy;
  stages: PipelineStageSummary[];
}

export interface PipelineStageCreate {
  name: string;
  stageNumber: number;
  color?: string | null;
  kind?: StageKind;
  staleAfterDays?: number | null;
  isTerminalWon?: boolean;
  isTerminalLost?: boolean;
  autoConvert?: boolean;
  automationSequence?: PipelineStageSequenceSave | null;
}

export interface PipelineStageUpdate {
  id?: string | null;
  name?: string | null;
  stageNumber?: number | null;
  color?: string | null;
  kind?: StageKind | null;
  staleAfterDays?: number | null;
  isTerminalWon?: boolean | null;
  isTerminalLost?: boolean | null;
  autoConvert?: boolean | null;
  /** Omitted leaves the stage's automations untouched; null removes them. */
  automationSequence?: PipelineStageSequenceSave | null;
}

export interface PipelineCreate {
  locationId?: string | null;
  name: string;
  description?: string;
  returningLeadPolicy?: ReturningLeadPolicy;
  stages?: PipelineStageCreate[];
}

export interface PipelineUpdate {
  locationId?: string | null;
  name?: string;
  description?: string | null;
  isActive?: boolean;
  returningLeadPolicy?: ReturningLeadPolicy;
  stages?: PipelineStageUpdate[];
}

// ============================================================================
// Platform & Campaign Types
// ============================================================================

export enum PlatformType {
  GOOGLE_ADS = "GOOGLE_ADS",
  META_ADS = "META_ADS",
  WEB_FORM = "WEB_FORM",
  CUSTOM = "CUSTOM",
}

export interface PlatformSummary {
  id: string;
  name: string;
  url?: string | null;
  icon?: string | null;
  isActive: boolean;
  platformType: PlatformType;
  isConnected: boolean;
}

export enum CampaignPostType {
  SOCIAL_POST = "SOCIAL_POST",
  AD = "AD",
  EMAIL = "EMAIL",
  BLOG = "BLOG",
  LANDING_PAGE = "LANDING_PAGE",
}

export enum CampaignPostStatus {
  DRAFT = "DRAFT",
  SCHEDULED = "SCHEDULED",
  PUBLISHED = "PUBLISHED",
  PAUSED = "PAUSED",
  ARCHIVED = "ARCHIVED",
}

export enum CampaignMediaType {
  IMAGE = "IMAGE",
  VIDEO = "VIDEO",
  DOCUMENT = "DOCUMENT",
}

export interface CampaignWebhookSummary {
  webhookKey: string;
  webhookUrl: string;
  redirectUrl?: string | null;
}

/** A campaign as a picker lists it: enough to choose it and say where its leads land. */
export interface CampaignOption {
  id: string;
  name: string;
  pipelineId?: string | null;
  pipelineName?: string | null;
}

export interface CampaignSummary {
  locationId?: string | null;
  id: string;
  name: string;
  description?: string | null;
  platformId?: string | null;
  platform?: PlatformSummary | null;
  platformAssetId?: string | null;
  externalCampaignId?: string | null;
  externalResourceName?: string | null;
  externalName?: string | null;
  externalStatus?: string | null;
  externalCurrency?: string | null;
  externalDailyBudgetCents?: number | null;
  externalLifetimeBudgetCents?: number | null;
  externalLastSeenAt?: string | null;
  externalLastSyncedAt?: string | null;
  externalStaleAt?: string | null;
  externalSyncError?: string | null;
  pipelineId?: string | null;
  isActive: boolean;
  startDate?: string | null;
  endDate?: string | null;
  budgetCents?: number | null;
  webhook?: CampaignWebhookSummary | null;
  createdDate: string;
  opportunityCount?: number;
  convertedCount?: number;
}

export interface CampaignDetail extends CampaignSummary {
  posts: CampaignPostSummary[];
}

export interface CampaignCreate {
  locationId?: string | null;
  name: string;
  description?: string;
  platformId?: string;
  pipelineId?: string;
  startDate?: string;
  endDate?: string;
  budgetCents?: number;
}

export interface CampaignUpdate {
  locationId?: string | null;
  name?: string;
  description?: string | null;
  platformId?: string | null;
  pipelineId?: string | null;
  isActive?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  budgetCents?: number | null;
}

export interface CampaignMediaSummary {
  id: string;
  mediaType: CampaignMediaType;
  gcsPath: string;
  fileName: string;
  fileSizeBytes?: number | null;
  mimeType?: string | null;
  durationSeconds?: number | null;
  width?: number | null;
  height?: number | null;
  thumbnailGcsPath?: string | null;
  displayOrder: number;
  altText?: string | null;
  signedUrl?: string | null;
}

export interface CampaignPostSummary {
  id: string;
  campaignId: string;
  platformId?: string | null;
  platform?: PlatformSummary | null;
  postType: CampaignPostType;
  title?: string | null;
  caption?: string | null;
  ctaText?: string | null;
  ctaUrl?: string | null;
  scheduledDate?: string | null;
  publishedDate?: string | null;
  externalPostId?: string | null;
  externalPostUrl?: string | null;
  status: CampaignPostStatus;
  creatorId: string;
  creatorName?: string | null;
  media: CampaignMediaSummary[];
  createdDate: string;
}

export interface CampaignPostCreate {
  campaignId: string;
  platformId?: string;
  postType: CampaignPostType;
  title?: string;
  caption?: string;
  ctaText?: string;
  ctaUrl?: string;
  scheduledDate?: string;
  status?: CampaignPostStatus;
}

export interface CampaignPostUpdate {
  platformId?: string | null;
  postType?: CampaignPostType;
  title?: string | null;
  caption?: string | null;
  ctaText?: string | null;
  ctaUrl?: string | null;
  scheduledDate?: string | null;
  status?: CampaignPostStatus;
}

// ============================================================================
// Opportunity Types
// ============================================================================

export enum OpportunityStatus {
  OPEN = "OPEN",
  WON = "WON",
  LOST = "LOST",
  DISQUALIFIED = "DISQUALIFIED",
}

export enum PatientLifecycle {
  PROSPECT = "PROSPECT",
  PATIENT = "PATIENT",
}

export enum StageKind {
  NEW = "NEW",
  WORKING = "WORKING",
  ENGAGED = "ENGAGED",
  BOOKED = "BOOKED",
  WON = "WON",
  LOST = "LOST",
}

export enum TouchpointChannel {
  META_LEAD_FORM = "META_LEAD_FORM",
  GOOGLE_LEAD_FORM = "GOOGLE_LEAD_FORM",
  PAID_CLICK = "PAID_CLICK",
  WEB_ORGANIC = "WEB_ORGANIC",
  WEB_FORM = "WEB_FORM",
  WEB_CHAT = "WEB_CHAT",
  PHONE_CALL = "PHONE_CALL",
  SMS_INBOUND = "SMS_INBOUND",
  EMAIL_INBOUND = "EMAIL_INBOUND",
  DM_INSTAGRAM = "DM_INSTAGRAM",
  DM_FACEBOOK = "DM_FACEBOOK",
  REFERRAL = "REFERRAL",
  WALK_IN = "WALK_IN",
  QR = "QR",
  IMPORT = "IMPORT",
  OTHER = "OTHER",
}

export interface OpportunityBookingSummary {
  conversionId: string;
  eventId: string;
  occurredAt: string;
  appointmentStart: string;
  appointmentEnd: string;
  appointmentStatus: EventStatus;
  locationName?: string | null;
  isLinked: boolean;
  isArchived: boolean;
}

// ============================================================================
// Opportunity Tags
// ============================================================================

/** A tag from the opportunity catalog. See `EntityTag` for the shared shape. */
export type OpportunityTag = EntityTag;
export type OpportunityTagCreate = EntityTagCreate;
export type OpportunityTagUpdate = EntityTagUpdate;

export interface OpportunityTagDetail extends OpportunityTag {
  opportunityCount?: number | null;
}

export interface OpportunitySummary {
  id: string;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  sourceDetail?: string | null;
  campaignId?: string | null;
  adSetId?: string | null;
  adId?: string | null;
  /** Campaign, ad set and ad, deepest last. Only levels that resolved. */
  attributionChain?: string[];
  campaign?: CampaignSummary | null;
  /** Null until someone enrols the opportunity on a pipeline. */
  pipelineId?: string | null;
  currentStageId?: string | null;
  currentStage?: PipelineStageSummary | null;
  patientId: string;
  convertedDate?: string | null;
  assignedProviderId?: string | null;
  assignedProvider?: ProviderTiny | null;
  status: OpportunityStatus;
  wonAt?: string | null;
  lostAt?: string | null;
  lostReasonId?: string | null;
  lostReasonLabel?: string | null;
  lostReason?: string | null;
  stageEnteredAt: string;
  sourceChannel?: TouchpointChannel | null;
  originatingTouchpointId?: string | null;
  estValueCents?: number | null;
  contactAttempts: number;
  lastOutreachAt?: string | null;
  lastActivityAt?: string | null;
  firstResponseDueAt?: string | null;
  firstResponseAt?: string | null;
  lastEngagedAt?: string | null;
  automationsPaused: boolean;
  stale: boolean;
  lifecycleStage: PatientLifecycle;
  isExistingPatient: boolean;
  optedInToSmsMarketing?: boolean | null;
  optedInToEmailMarketing?: boolean | null;
  latestBooking?: OpportunityBookingSummary | null;
  /**
   * The tags in effect for this opportunity. Resolved server-side by the
   * opportunity tag scope, which by default treats tags as the patient's — so
   * these can include tags applied on another of their opportunities.
   */
  tags: OpportunityTag[];
  createdDate: string;
  updatedDate?: string | null;
}

export interface OpportunityCreate {
  firstName: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  sourceDetail?: string;
  campaignId?: string;
  locationId?: string;
  patientId?: string;
  pipelineId?: string;
  currentStageId?: string;
  /**
   * Create the opportunity with no pipeline at all. Leaving ``pipelineId``
   * out instead routes it by campaign, or to the clinic's default pipeline
   * when there is no campaign, so choosing not to enrol has to be explicit.
   */
  unassigned?: boolean;
  assignedProviderId?: string;
  notes?: string;
  optedInToSmsMarketing?: boolean;
  optedInToEmailMarketing?: boolean;
}

export interface OpportunityBulkCreateRow {
  rowNumber?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  sourceDetail?: string;
  notes?: string;
}

export interface OpportunityBulkCreate {
  opportunities: OpportunityBulkCreateRow[];
  pipelineId?: string;
  currentStageId?: string;
  campaignId?: string;
  locationId?: string;
  optedInToSmsMarketing?: boolean;
  optedInToEmailMarketing?: boolean;
}

export interface OpportunityBulkCreateError {
  rowNumber: number;
  field?: string | null;
  message: string;
}

export interface OpportunityBulkCreateResponse {
  totalCount: number;
  createdCount: number;
  bumpedCount: number;
  bumpedIds: string[];
  failedCount: number;
  opportunities: OpportunitySummary[];
  errors: OpportunityBulkCreateError[];
}

export interface OpportunityUpdate {
  firstName?: string;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  campaignId?: string | null;
  currentStageId?: string | null;
  assignedProviderId?: string | null;
  lostReasonId?: string | null;
  notes?: string | null;
  optedInToSmsMarketing?: boolean;
  optedInToEmailMarketing?: boolean;
}

export interface OpportunityStageUpdate {
  stageId: string;
  lostReasonId?: string;
}

export interface OpportunityStatusUpdate {
  status: OpportunityStatus;
  lostReasonId?: string;
  resumeAutomations?: boolean;
}

export interface OpportunityAssignmentUpdate {
  assignedProviderId?: string | null;
}

export interface OpportunityEnrollmentUpdate {
  pipelineId: string;
  /** Omitted lands the opportunity on the pipeline's first stage. */
  currentStageId?: string;
}

export interface LostReasonSummary {
  id: string;
  label: string;
  kind: "LOST" | "DISQUALIFIED";
  position: number;
}

export interface OpportunityNoteSummary {
  id: string;
  opportunityId: string;
  content: string;
  creatorId: string;
  creator?: ProviderTiny;
  createdDate: string;
}

export interface OpportunityNoteCreate {
  content: string;
}

export interface OpportunityAISummarySummary {
  id: string;
  opportunityId: string;
  summary: string;
  suggestions?: string[] | null;
  latestActivity?: string | null;
  createdDate: string;
}

export interface OpportunityDetail extends OpportunitySummary {
  notes?: string | null;
  opportunityNotes: OpportunityNoteSummary[];
  aiSummaries: OpportunityAISummarySummary[];
  bookings: OpportunityBookingSummary[];
}

export interface OpportunityFetchParams extends PageParams {
  pipelineIds?: string[];
  currentStageIds?: string[];
  campaignIds?: string[];
  assignedProviderIds?: string[];
  statuses?: OpportunityStatus[];
  sourceChannels?: TouchpointChannel[];
  search?: string;
  isConverted?: boolean;
  contactRecency?: ContactRecency;
  contactClock?: ContactClock;
}

// ============================================================================
// Grouped table
// ============================================================================

/** Column the opportunity table buckets rows by. Mirrors the backend enum. */
export enum OpportunityGroupBy {
  CAMPAIGN = "campaign",
  PIPELINE = "pipeline",
  STAGE = "stage",
  ASSIGNED_PROVIDER = "assigned_provider",
  STATUS = "status",
}

/**
 * Group keys the backend reserves for the "nothing set" bucket. They double as
 * the list filter that selects that bucket, which is what lets an empty bucket
 * paginate like any named one.
 */
export const OPPORTUNITY_GROUP_NONE_KEYS = {
  [OpportunityGroupBy.CAMPAIGN]: "__manual__",
  [OpportunityGroupBy.PIPELINE]: "__unassigned__",
  [OpportunityGroupBy.STAGE]: "__none__",
  [OpportunityGroupBy.ASSIGNED_PROVIDER]: "__none__",
  [OpportunityGroupBy.STATUS]: "",
} as const;

export interface OpportunityGroupItem {
  key: string;
  label: string;
  total: number;
}

// ============================================================================
// Bulk actions
// ============================================================================

export interface OpportunityBulkActionFailure {
  opportunityId: string;
  reason: string;
}

export interface OpportunityBulkActionResult {
  succeeded: number;
  skipped: number;
  failed: OpportunityBulkActionFailure[];
}

export interface OpportunityBulkStatusUpdate {
  opportunityIds: string[];
  status: OpportunityStatus;
  lostReasonId?: string | null;
}

export interface OpportunityBulkArchive {
  opportunityIds: string[];
  isArchived?: boolean;
}

export interface OpportunityBulkMarketingConsent {
  opportunityIds: string[];
  optedInToSmsMarketing?: boolean;
  optedInToEmailMarketing?: boolean;
}

export interface OpportunityBulkCampaignUpdate {
  opportunityIds: string[];
  campaignId: string | null;
}

export interface LostReasonCreate {
  label: string;
  kind: "LOST" | "DISQUALIFIED";
}

// ============================================================================
// Opportunity Log Types
// ============================================================================

export enum OpportunityLogAction {
  CREATED = "CREATED",
  REMOVED = "REMOVED",
  CONVERTED = "CONVERTED",
  MERGED = "MERGED",

  STAGE_CHANGED = "STAGE_CHANGED",
  PIPELINE_ENROLLED = "PIPELINE_ENROLLED",
  CAMPAIGN_CHANGED = "CAMPAIGN_CHANGED",
  PROVIDER_ASSIGNED = "PROVIDER_ASSIGNED",
  PROVIDER_UNASSIGNED = "PROVIDER_UNASSIGNED",
  WON = "WON",
  LOST = "LOST",
  DISQUALIFIED = "DISQUALIFIED",
  REOPENED = "REOPENED",
  AUTOMATIONS_PAUSED = "AUTOMATIONS_PAUSED",
  AUTOMATIONS_RESUMED = "AUTOMATIONS_RESUMED",
  LOST_REASON_CHANGED = "LOST_REASON_CHANGED",
  ARCHIVED = "ARCHIVED",
  UNARCHIVED = "UNARCHIVED",
  RETURNED = "RETURNED",
  RESTARTED = "RESTARTED",

  PERSON_DETAILS_UPDATED = "PERSON_DETAILS_UPDATED",
  SMS_MARKETING_OPTED_IN = "SMS_MARKETING_OPTED_IN",
  SMS_MARKETING_OPTED_OUT = "SMS_MARKETING_OPTED_OUT",
  EMAIL_MARKETING_OPTED_IN = "EMAIL_MARKETING_OPTED_IN",
  EMAIL_MARKETING_OPTED_OUT = "EMAIL_MARKETING_OPTED_OUT",

  NOTE_ADDED = "NOTE_ADDED",
  ACTION_EXECUTED = "ACTION_EXECUTED",
  ACTION_SKIPPED = "ACTION_SKIPPED",
  ACTION_FAILED = "ACTION_FAILED",
  CONTACT_LOGGED = "CONTACT_LOGGED",
  TAGS_CHANGED = "TAGS_CHANGED",

  FIELD_CHANGED = "FIELD_CHANGED",
}

export enum ContactChannel {
  CALL = "CALL",
  SMS = "SMS",
  EMAIL = "EMAIL",
  OTHER = "OTHER",
}

export enum ContactDirection {
  OUTBOUND = "OUTBOUND",
  INBOUND = "INBOUND",
}

export enum ContactOutcome {
  CONNECTED = "CONNECTED",
  NO_ANSWER = "NO_ANSWER",
  LEFT_VOICEMAIL = "LEFT_VOICEMAIL",
  BUSY = "BUSY",
  WRONG_NUMBER = "WRONG_NUMBER",
  BAD_NUMBER = "BAD_NUMBER",
}

export enum ContactClock {
  LAST_OUTREACH = "LAST_OUTREACH",
  LAST_ACTIVITY = "LAST_ACTIVITY",
}

export enum ContactRecency {
  NEVER_CONTACTED = "NEVER_CONTACTED",
  CONTACTED_TODAY = "CONTACTED_TODAY",
  COLD_3_DAYS = "COLD_3_DAYS",
  COLD_7_DAYS = "COLD_7_DAYS",
  COLD_14_DAYS = "COLD_14_DAYS",
  COLD_30_DAYS = "COLD_30_DAYS",
}

export enum OpportunityLogSource {
  USER = "USER",
  AUTOMATION = "AUTOMATION",
  SEQUENCE = "SEQUENCE",
  IMPORT = "IMPORT",
  API = "API",
  AI_ASSISTANT = "AI_ASSISTANT",
}

export interface OpportunityLogDetail {
  id: string;
  opportunityId: string;
  action: OpportunityLogAction;
  source: OpportunityLogSource;
  creatorId?: string | null;
  creator?: ProviderTiny | null;
  occurredAt?: string | null;
  batchId?: string | null;
  note?: string | null;

  pipelineStageId?: string | null;
  previousStageId?: string | null;
  pipelineId?: string | null;
  campaignId?: string | null;
  providerId?: string | null;
  lostReasonId?: string | null;
  mergedIntoId?: string | null;
  noteId?: string | null;

  channel?: ContactChannel | null;
  direction?: ContactDirection | null;
  outcome?: ContactOutcome | null;
  durationSeconds?: number | null;

  pipelineStageName?: string | null;
  previousStageName?: string | null;
  pipelineName?: string | null;
  campaignName?: string | null;
  lostReasonLabel?: string | null;
  provider?: ProviderTiny | null;

  fieldName?: string | null;
  oldValue?: string | null;
  newValue?: string | null;

  createdDate: string;
}

export interface OpportunityContactLogCreate {
  channel: ContactChannel;
  direction?: ContactDirection;
  outcome?: ContactOutcome | null;
  durationSeconds?: number | null;
  occurredAt?: string | null;
  note?: string | null;
}

// ============================================================================
// Analytics Types
// ============================================================================

export interface OpportunitiesByStage {
  stageId: string;
  stageName: string;
  color?: string | null;
  count: number;
}

export interface OpportunitiesBySource {
  source: string;
  count: number;
  revenueCents: number;
}

export interface OpportunitiesOverTime {
  date: string;
  count: number;
}

export interface DashboardKPIs {
  newOpportunities: number;
  convertedOpportunities: number;
  lostOpportunities: number;
  avgConversionDays?: number | null;
  opportunitiesByStage: OpportunitiesByStage[];
  opportunitiesBySource: OpportunitiesBySource[];
  opportunitiesOverTime: OpportunitiesOverTime[];
}

export interface CampaignMetric {
  campaignId: string;
  campaignName: string;
  isActive: boolean;
  budgetCents?: number | null;
  spendCents: number;
  opportunityCount: number;
  convertedCount: number;
  cpaCents?: number | null;
  revenueCents?: number | null;
  avgLtvCents?: number | null;
  roiPercent?: number | null;
}

export interface CampaignMetricsResponse {
  campaigns: CampaignMetric[];
}

export enum CampaignDeliveryStatus {
  AVAILABLE = "AVAILABLE",
  LIMITED = "LIMITED",
  UNAVAILABLE = "UNAVAILABLE",
  NOT_VALIDATED = "NOT_VALIDATED",
}

export interface CampaignMetricSnapshot {
  id: string;
  campaignId: string;
  date: string;
  impressions?: number | null;
  clicks?: number | null;
  interactions?: number | null;
  reach?: number | null;
  frequency?: number | null;
  spendCents?: number | null;
  conversions?: number | null;
  leadsGenerated?: number | null;
  videoViews?: number | null;
  currency?: string | null;
  metricCoverage: string[];
}

export interface CampaignDeliveryDateRange {
  startDate: string;
  endDate: string;
}

export interface CampaignPerformanceSummary {
  dataStatus: CampaignDeliveryStatus;
  provider?: PlatformType | null;
  providerName?: string | null;
  metricCoverage: string[];
  lastAttemptedAt?: string | null;
  lastSyncedAt?: string | null;
  coverageStart?: string | null;
  coverageEnd?: string | null;
  lastError?: string | null;
  providerUrl?: string | null;
  isStale: boolean;
  dateRangeStart: string;
  dateRangeEnd: string;
  totalImpressions?: number | null;
  totalClicks?: number | null;
  totalInteractions?: number | null;
  totalReach?: number | null;
  frequency?: number | null;
  totalSpendCents?: number | null;
  totalConversions?: number | null;
  totalLeadsGenerated?: number | null;
  totalVideoViews?: number | null;
  ctr?: number | null;
  cpcCents?: number | null;
  cpmCents?: number | null;
  daily: CampaignMetricSnapshot[];
}
